import { IntlShape } from 'react-intl'
import { Dimensions, ImageSourcePropType, Platform } from 'react-native'

import isChromatic from 'chromatic/isChromatic'
import * as Application from 'expo-application'
import { brand } from 'expo-device'
import { AuthenticationType } from 'expo-local-authentication'
import * as MailComposer from 'expo-mail-composer'
import { isNil } from 'lodash-es'
import moment from 'moment-timezone'
import styled from 'styled-components/native'

import {
  CalendarCardAppointment,
  CustomNotificationsDateSettings,
  CustomNotificationsPreferences,
  Episode,
  isWithin24Hours,
  NotificationMethod,
  OptionalNotificationMethods,
  PROGRAM_SESSION_RESCHEDULE_MAP,
  ProgramNames,
} from '@lyrahealth-inc/shared-app-logic'

import { Linking } from './expo-linking'
import { aiCoachProviderInfo, BiometricsTypes, IS_WEB } from '../constants'
import { TextAlign } from '../styles'

/**
 * Avoid adding @data types here.
 * Types that are data specific should not live in ui-core-crossplatform
 */

// determine which biometrics string we should display for the user
export const getBiometricType = (authType?: AuthenticationType | null): BiometricsTypes | null => {
  switch (authType) {
    case AuthenticationType.FACIAL_RECOGNITION:
      return Platform.OS === 'ios' ? BiometricsTypes.FaceID : BiometricsTypes.FaceUnlock
    case AuthenticationType.FINGERPRINT:
      return Platform.OS === 'ios' ? BiometricsTypes.TouchID : BiometricsTypes.Fingerprint
    default:
      return null
  }
}

// friendly format the time stamp
// make sure to always keep timestamp even though we don't display it
export const getFriendlyFormattedTimeStamp = (intl: IntlShape, rawTimeStamp: string): string => {
  if (rawTimeStamp) {
    // Since we are using moment to help calculate time elapsed from passsed timestamp, timezone can not be undefined here.
    // 'moment.tz.guess' is added here to satisfy the linter because intl.timeZone will not be undefined unless user has not logged in
    // or has not done search for care at least once. In both those cases we do not show any dates/times and hence this function is not called.
    const displayedTimezone = intl.timeZone ?? moment.tz.guess(true)
    const ref = moment.tz(displayedTimezone)

    const today = ref.clone().startOf('day')
    const yesterday = ref.clone().subtract(1, 'days').startOf('day')
    const tomorrow = ref.clone().add(1, 'days').startOf('day')

    const timeStamp = moment(rawTimeStamp).tz(displayedTimezone)

    const isSameDay = timeStamp.isSame(today, 'day')
    const isYesterday = timeStamp.isSame(yesterday, 'day')
    const isTomorrow = timeStamp.isSame(tomorrow, 'day')

    const isWithinAWeek = moment().diff(timeStamp, 'days') >= 1 && moment().diff(timeStamp, 'days') <= 7

    // if the last message was sent today show as 1:00 pm format
    if (isSameDay) {
      return intl.formatTime(rawTimeStamp)
      // if last message was sent with 7 days
    } else if (isYesterday) {
      return intl.formatMessage({
        defaultMessage: 'Yesterday',
        description: 'The day before today.',
      })
    } else if (isTomorrow) {
      return intl.formatMessage({
        defaultMessage: 'Tomorrow',
        description: 'The day after today.',
      })
    } else if (isWithinAWeek) {
      return intl.formatDate(rawTimeStamp, { weekday: 'long' })
    } else {
      return intl.formatDate(rawTimeStamp, { day: '2-digit', year: 'numeric', month: '2-digit' })
    }
  }
  return ''
}

export const getFriendlyFormattedTimeStampComment = (intl: IntlShape, rawTimeStamp: string): string => {
  return `${intl.formatDate(rawTimeStamp, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })}`
}

/**
 * Calculate and format the benefit end date based on session count start date.
 * Assumes end date is sessionCountStartsOnDate +1 year and -1 day.
 * @param sessionCountStartsOnDate The date when the session count starts
 * @param formatDate The intl formatDate function
 * @returns Formatted benefit end date string (e.g., "December 31, 2025") or empty string if no start date
 */
export const getBenefitEndDate = (
  sessionCountStartsOnDate: Date | undefined,
  formatDate: IntlShape['formatDate'],
): string => {
  if (!sessionCountStartsOnDate) return ''

  const benefitEndDate = new Date(sessionCountStartsOnDate)
  benefitEndDate.setFullYear(new Date().getFullYear() + 1)
  benefitEndDate.setDate(benefitEndDate.getDate() - 1)
  const formattedBenefitEndDate = formatDate(benefitEndDate, {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  return formattedBenefitEndDate
}

export const getEmptyMessageContent = (displayName: string): string =>
  `👋 Your messages with ${displayName} will appear here.`

/**
 * Get an array of active notification methods for a given channel name
 * If all channels are inactive allOff will be true
 * @param channelName name of notification channel
 * @param notificationMethods notification method object
 */
export const getActiveNotificationChannelMethods = ({
  channelName,
  notificationMethods,
}: {
  channelName: string
  notificationMethods?: OptionalNotificationMethods
}): [string[], boolean] => {
  const activeMethods = Object.keys(NotificationMethod).filter(
    (methodKey) => notificationMethods?.[channelName][NotificationMethod[methodKey]],
  )
  const allOff = activeMethods?.length === 0
  return [activeMethods, allOff]
}

export const getDayOfWeekFromNumber = (dayNumber?: number) => {
  switch (dayNumber) {
    case 0:
      return 'Monday'
    case 1:
      return 'Tuesday'
    case 2:
      return 'Wednesday'
    case 3:
      return 'Thursday'
    case 4:
      return 'Friday'
    case 5:
      return 'Saturday'
    case 6:
      return 'Sunday'
    default:
      return ''
  }
}

export const getCustomDayAsNumber = (day?: string) => {
  switch (day) {
    case 'Monday':
      return 0
    case 'Tuesday':
      return 1
    case 'Wednesday':
      return 2
    case 'Thursday':
      return 3
    case 'Friday':
      return 4
    case 'Saturday':
      return 5
    case 'Sunday':
      return 6
    default:
      return ''
  }
}

/**
 * Get the type of Custom Reminder that the user saved, if any
 * @param customNotificationPreferences notification preferences object
 */
export const getCustomNotificationType = ({
  customNotificationPreferences,
}: {
  customNotificationPreferences?: CustomNotificationsPreferences
}) => {
  if (!customNotificationPreferences) {
    return ''
  }
  const dateSetting = customNotificationPreferences?.date_setting
  if (dateSetting === CustomNotificationsDateSettings.DAY_BEFORE) {
    return 'Day before'
  } else if (dateSetting === CustomNotificationsDateSettings.DAY_OF) {
    return 'Morning of'
  } else if (dateSetting === CustomNotificationsDateSettings.DAY_OF_THE_WEEK) {
    return `${
      !isNil(customNotificationPreferences.day_of_the_week) &&
      Number.isInteger(customNotificationPreferences?.day_of_the_week)
        ? getDayOfWeekFromNumber(customNotificationPreferences.day_of_the_week as number)
        : customNotificationPreferences.day_of_the_week
    }`
  }
  return ''
}

/**
 * Helper function to prefix the package name to the testID
 * Appium requires the package name to be part of the resource-id(value is mapped from testID for android)
 * itself otherwise it will fail to find it
 * @param testID string
 */
export function getTestID(testID: string | undefined) {
  const appIdentifier = Application.applicationId

  if (!testID) {
    return undefined
  }

  const prefix = `${appIdentifier}:id/`
  const hasPrefix = testID.startsWith(prefix)

  return Platform.select({
    android: !hasPrefix ? `${prefix}${testID}` : testID,
    ios: hasPrefix ? testID.slice(prefix.length) : testID,
    web: hasPrefix ? testID.slice(prefix.length) : testID,
  })
}

export const tID = getTestID

/**
 * Helper function to determine if content is extending past device screen height
 * @param layout LayoutRectangle
 */
export function isContentOffScreenVertically(viewHeight: number) {
  const screenHeight = Dimensions.get('screen').height
  return viewHeight > screenHeight
}

/**
 * Determine if a device is made by Samsung.
 * This is used to fix a bug related to how Samsung devices handle the content menu
 */
export const deviceIsSamsung = () => brand === 'samsung'

/**
 * MailComposer will open mail modal on iOS and app on android
 * Fallback on Linking if it is blocked by user: https://docs.expo.io/versions/latest/sdk/mail-composer/
 */
export const mailTo = async (emailAddress: string) => {
  const mailComposerOptions = {
    recipients: [emailAddress],
  }
  return (await MailComposer.isAvailableAsync())
    ? MailComposer.composeAsync(mailComposerOptions)
    : Linking.openURL(`mailto:${emailAddress}`)
}

/**
 * Checks if running in a Storybook test environment (Chromatic or test-runner)
 * This is used to disable animations
 */
export const isStorybookTest = IS_WEB
  ? () =>
      isChromatic() ||
      (typeof window !== 'undefined' &&
        ((window as { __STORYBOOK_TEST_RUNNER__?: boolean }).__STORYBOOK_TEST_RUNNER__ === true ||
          window.location.href.includes('testRunner=true')))
  : () => false

/**
 * @deprecated Use isStorybookTest instead
 * Checks if using Chromatic on the web
 */
export const isChromaticWeb = isStorybookTest

/**
 * Web utilizes navigatation via lat/lng if values exist, otherwise navigate by the street address
 */
export const openHyperlinkForDirectionsToAddress = (address: string, lat?: number, lng?: number) => {
  const webLinkLatLng = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
  const webLinkStreet = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  const url =
    Platform.select({
      ios: `maps:0,0?q=${encodeURIComponent(address)}`,
      android: `geo:0,0?q=${encodeURIComponent(address)}`,
      web: lat ? webLinkLatLng : webLinkStreet,
    }) ?? ''
  return IS_WEB ? window.open(url) : Linking.openURL(url)
}

interface ActionSheetOptions {
  options: string[]
  destructiveButtonIndex: number
  cancelButtonIndex: number
}

export const isPatientInEpisodeType = (episodes: Episode[], programType: ProgramNames) => {
  return episodes?.length > 0 && episodes.find((episode: Episode) => episode?.program_name === programType)
}

export const onUpcomingSessionPress = (
  session: CalendarCardAppointment,
  programName: ProgramNames,
  showActionSheetWithOptions: (options: ActionSheetOptions, callback: (i: number) => void) => void,
  navigateToRescheduleSession: (appointment: CalendarCardAppointment | number) => void,
  onCancelSessionPress: (appointment: CalendarCardAppointment) => void,
) => {
  const programAllowsReschedule = PROGRAM_SESSION_RESCHEDULE_MAP[programName]
  const canReschedule = !isWithin24Hours(session) && programAllowsReschedule
  if (canReschedule) {
    showActionSheetWithOptions(
      {
        options: ['Reschedule session', 'Cancel session', 'Close'],
        destructiveButtonIndex: 1,
        cancelButtonIndex: 2,
      },
      (buttonIndex: number) => {
        if (buttonIndex === 0) {
          navigateToRescheduleSession && navigateToRescheduleSession(session)
        } else if (buttonIndex === 1) {
          onCancelSessionPress(session as unknown as CalendarCardAppointment)
        }
      },
    )
  } else {
    showActionSheetWithOptions(
      {
        options: ['Cancel session', 'Close'],
        destructiveButtonIndex: 1,
        cancelButtonIndex: 2,
      },
      (buttonIndex: number) => {
        if (buttonIndex === 0) {
          onCancelSessionPress(session as unknown as CalendarCardAppointment)
        }
      },
    )
  }
}

/**
 * Put this after the onPress function in the form action button to scroll the view
 * to the first input that has an error message.
 *
 * Only needed for Web since Native handles this out of the box
 */
export const scrollToFormInputErrorWeb = () => {
  if (IS_WEB) {
    const FORM_ERROR_SUBSTRINGS = [
      'errorBanner',
      'error-message',
      'userAgeBelowMinError',
      'eligibilityErrorBanner',
      'EmployeeInfoForDependentEligibility-tips',
    ]

    const firstError = Array.from(document.querySelectorAll('[data-testid]')).find((el) => {
      const testId = el.getAttribute('data-testid')
      return testId ? FORM_ERROR_SUBSTRINGS.find((error) => testId.includes(error)) : false
    })
    if (firstError) {
      firstError.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      })
    }
  }
}

export const scrollToTestIdWeb = (testId: string) => {
  if (IS_WEB) {
    const testIdElement = document.querySelector(`[data-testid="${testId}"]`)
    if (testIdElement) {
      testIdElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      })
    }
  }
}

export const getFlexAlignFromTA = (ta: TextAlign) => {
  switch (ta) {
    case 'left':
      return 'flex-start'
    case 'right':
      return 'flex-end'
    case 'center':
      return 'center'
    default:
      return 'flex-start'
  }
}

export const decodeHtmlEntities = (textWithPossibleHTML: string) => {
  if (!IS_WEB) {
    return textWithPossibleHTML
  }
  // using the built in DOM to decode
  const txt = document.createElement('textarea')
  txt.innerHTML = textWithPossibleHTML
  return txt.value
}

export const TabBarSpacer = styled.View<{
  tabBarHeight: number
}>(({ tabBarHeight }) => ({
  height: tabBarHeight,
  width: '100%',
  zIndex: -1,
}))

export const getAiCoachProvider = ({ providerLyraId }: { providerLyraId: string }) => {
  return { provider: { ...aiCoachProviderInfo, lyra_id: providerLyraId }, appointments: [] }
  /**
   * Converts an asset string or number (either asset name or URI) to an ImageSource
   * @param asset - Either a string name, number, or URI of the asset
   * @returns ImageSourcePropType or the original string/number passed
   */
}

export const getAssetSource = (asset: string | number): ImageSourcePropType | string => {
  // Check if it's already a URI (starts with http/https)
  if (typeof asset === 'string' && (asset.startsWith('http') || asset.startsWith('https'))) {
    return { uri: asset }
  }

  // Otherwise, treat it as a local asset name
  return asset
}
